import { useState } from 'react';
import { Search, Sparkles, Database, Github, Loader2, CheckCircle2, Brain } from 'lucide-react';
import { KnowledgeGraphVisualization } from '../components/KnowledgeGraphVisualization';
import { API_URL } from '../lib/api';

const GRAPH_QA_URL = `${API_URL}/api`;

interface GraphNode {
  type: string;
  id: string;
  properties: Record<string, any>;
}

interface GraphEdge {
  type: string;
  source: string;
  target: string;
  properties?: Record<string, any>;
}

interface GraphQAResponse {
  success: boolean;
  answer: string;
  reasoning: string;
  sources: string[];
  confidence: number;
  graph_context?: {
    nodes: GraphNode[];
    edges: GraphEdge[];
  };
  graph_stats: {
    nodes: number;
    edges: number;
    node_types: string[];
    edge_types: string[];
  };
  model: string;
  error?: string;
}

type ContextType = 'erc8004' | 'github';

export function GraphQAPage() {
  const [question, setQuestion] = useState('');
  const [contextType, setContextType] = useState<ContextType>('erc8004');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<GraphQAResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const exampleQuestions = {
    erc8004: [
      'Which agents can tell me about movies?',
      'Which agents have blockchain capabilities?',
      'What agents can analyze DeFi protocols?',
      'Which agents support x402 payments?',
      'What skills does agent 11155111:1032 have?',
    ],
    github: [
      'What are the most starred Solana repositories?',
      'Which projects are related to Ethereum?',
      'What are the top DeFi projects?',
      'Which repositories contain smart contracts?',
    ],
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!question.trim()) {
      setError('Please enter a question');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch(`${GRAPH_QA_URL}/graph-qa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question: question.trim(),
          context_type: contextType,
          search_term: searchTerm.trim() || undefined,
          max_nodes: 100,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to get answer');
      }

      setResult(data);
    } catch (err) {
      console.error('[Graph QA Error]', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExampleClick = (exampleQuestion: string) => {
    setQuestion(exampleQuestion);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Search Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <form onSubmit={handleSubmit}>
            {/* Context Type Selector */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Knowledge Graph Context
              </label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setContextType('erc8004')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                    contextType === 'erc8004'
                      ? 'border-purple-600 bg-purple-50 text-purple-700'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Database className="w-5 h-5" />
                  <span className="font-medium">ERC-8004 Agents</span>
                </button>
                <button
                  type="button"
                  onClick={() => setContextType('github')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                    contextType === 'github'
                      ? 'border-purple-600 bg-purple-50 text-purple-700'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Github className="w-5 h-5" />
                  <span className="font-medium">GitHub Projects</span>
                </button>
              </div>
            </div>

            {/* Optional Search Filter */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter (optional)
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={
                  contextType === 'erc8004'
                    ? 'e.g., blockchain, defi, specific agent ID'
                    : 'e.g., solana, ethereum, defi'
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Question Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Question
              </label>
              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Ask anything about the knowledge graph..."
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Example Questions */}
            <div className="mb-4">
              <p className="text-xs font-medium text-gray-500 mb-2">Example questions:</p>
              <div className="flex flex-wrap gap-2">
                {exampleQuestions[contextType].map((example, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleExampleClick(example)}
                    className="text-xs px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || !question.trim()}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Analyzing Knowledge Graph...</span>
                </>
              ) : (
                <>
                  <Search className="w-5 h-5" />
                  <span>Ask Question</span>
                </>
              )}
            </button>
          </form>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {/* Results Display */}
        {result && (
          <div className="space-y-4">
            {/* Knowledge Graph Visualization - FIRST */}
            {result.graph_context && result.graph_context.nodes.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Knowledge Graph ({result.graph_stats.nodes} nodes, {result.graph_stats.edges} edges)
                </h3>
                <div style={{ height: '800px' }}>
                  <KnowledgeGraphVisualization
                    graph={{
                      nodes: result.graph_context.nodes.map(node => ({
                        id: node.id,
                        type: node.type.toLowerCase() as any,
                        label: node.properties.name || node.properties.symbol || node.id,
                        properties: node.properties
                      })),
                      edges: result.graph_context.edges.map((edge, idx) => ({
                        id: `edge-${idx}`,
                        source: edge.source,
                        target: edge.target,
                        type: edge.type.toLowerCase() as any,
                        label: edge.type,
                        properties: edge.properties
                      }))
                    }}
                  />
                </div>
              </div>
            )}

            {/* Answer Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                  <h2 className="text-xl font-semibold text-gray-900">Answer</h2>
                </div>
                <div className="flex items-center gap-2 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                  <Sparkles className="w-4 h-4" />
                  {(result.confidence * 100).toFixed(0)}% confident
                </div>
              </div>

              <div className="prose prose-sm max-w-none">
                <p className="text-gray-900 leading-relaxed whitespace-pre-wrap">
                  {result.answer}
                </p>
              </div>
            </div>

            {/* Reasoning Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Brain className="w-5 h-5 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">Reasoning</h3>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">{result.reasoning}</p>
            </div>

            {/* Sources Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Sources ({result.sources.length})
              </h3>
              <div className="flex flex-wrap gap-2">
                {result.sources.map((source, idx) => (
                  <div
                    key={idx}
                    className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-mono"
                  >
                    {source}
                  </div>
                ))}
              </div>
            </div>

            {/* Graph Stats Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Knowledge Graph Context
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {result.graph_stats.nodes}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">Nodes Retrieved</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {result.graph_stats.edges}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">Edges Retrieved</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {result.graph_stats.node_types.length}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">Node Types</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {result.graph_stats.edge_types.length}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">Edge Types</div>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>Node Types: {result.graph_stats.node_types.join(', ')}</span>
                  <span className="font-mono">{result.model}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!result && !error && !isLoading && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Sparkles className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Ask a question to get started
            </h3>
            <p className="text-sm text-gray-600">
              Select a context type, enter your question, and explore the knowledge graph
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
